import {
   SystemFieldEnum,
   RecordUtils,
   StageStore,
   UserStore,
   TemplateDetailsStatusEnum,
   CustomObjectModel,
   CustomObjectTemplateStore,
   CustomObjectTemplateType,
   CustomObjectStore,
   CustomObjectTemplateModel,
   FieldsStore,
   GOFResponse,
   UnionFieldModelType,
   Stage
} from '@flowwai-frontend/shared/sales-crm/sales-crm-core'
import {
   AddAnotherGoFResult,
   CustomObjectTemplateProperties,
   FieldResponse,
   SystemFieldEnumResponseResult
} from '@flowwai-frontend/bps-officer-web/graphql'

import { Common_CustomObjectTemplate_FragmentFragment } from '../fragments/pipelineItemTemplateFragment.generatedTypes'
import { convertToGoFWithFieldsType } from '../utils/TransformationUtils/TransformationUtils'
import { getTransformedFieldResponse } from '../utils/FieldResponseUtils'

import { convertGraphqlFieldResponseToUnionFieldResponse } from './fields'

interface StoreCustomObjectToCustomObjectModelProps {
   customObjectId: string
   customObjectTemplateId: string
   customObjectStore: CustomObjectStore
}

interface StoreCustomObjectFieldResponsesInLeadFunctionProps {
   fieldResponse: SystemFieldEnumResponseResult
   customObjectModel: CustomObjectModel
   userStore: UserStore
   stageStore: StageStore
}

export const storeCustomObjectAssigneeInUserStore = (
   transformedFieldResponse: any,
   userStore: UserStore,
   fieldResponse: FieldResponse
) => {
   const isFieldCustomObjectAssigneeField =
      fieldResponse.systemFieldEnum === SystemFieldEnum.CustomObjectAssignee
   if (isFieldCustomObjectAssigneeField) {
      userStore.createOrUpdateUser({
         ...transformedFieldResponse.response
      })
   }
}

export const setFieldResponseInCustomObjectModel = (
   field: UnionFieldModelType,
   customObjectModel: CustomObjectModel,
   stageStore: StageStore,
   fieldResponse: FieldResponse,
   userStore: UserStore,
   gofResponseId?: string
) => {
   if (!field.gof) throw new Error('Gof is not found for field')

   const transformedFieldResponse = convertGraphqlFieldResponseToUnionFieldResponse(
      field,
      fieldResponse
   )

   const gofId = field.gof.gofId
   const dummyGofResponseId = RecordUtils.getGoFResponseId(
      customObjectModel.customObjectId,
      gofId
   )

   const isCustomObjectStage =
      field.systemFieldEnum === SystemFieldEnum.CustomObjectStage
   if (isCustomObjectStage)
      stageStore.createOrUpdateStage({
         ...(transformedFieldResponse.response as Stage)
      })

   customObjectModel.createOrUpdateFieldResponse(
      gofId,
      gofResponseId || dummyGofResponseId,
      transformedFieldResponse
   )

   storeCustomObjectAssigneeInUserStore(
      transformedFieldResponse,
      userStore,
      fieldResponse
   )
}

const storeCustomObjectStageInStageStore = (
   transformedFieldResponse: any,
   stageStore: StageStore,
   fieldResponse: FieldResponse
) => {
   const isCustomObjectStageField =
      fieldResponse.systemFieldEnum === SystemFieldEnum.CustomObjectStage

   if (isCustomObjectStageField) {
      stageStore.createOrUpdateStage({
         ...transformedFieldResponse.response
      })
   }
}

export const storeCustomObjectFieldResponsesInCustomObjectModel = (
   props: StoreCustomObjectFieldResponsesInLeadFunctionProps
) => {
   const { fieldResponse, customObjectModel, userStore, stageStore } = props

   const isFieldResponseType = fieldResponse.__typename === 'FieldResponse'
   if (!isFieldResponseType) return

   const fieldModel = customObjectModel.customObjectTemplate.fields.find(
      field => field.fieldId === fieldResponse.fieldId
   )

   if (!fieldModel?.gof) {
      throw new Error(`${fieldModel?.fieldId}: GOF is undefined in the Field`)
   }

   const gofId = fieldModel.gof.gofId
   const entityId = customObjectModel.customObjectId
   const gofResponseId = RecordUtils.getGoFResponseId(entityId, gofId)

   const transformedFieldResponse = convertGraphqlFieldResponseToUnionFieldResponse(
      fieldModel,
      fieldResponse
   )

   storeCustomObjectAssigneeInUserStore(
      transformedFieldResponse,
      userStore,
      fieldResponse
   )

   storeCustomObjectStageInStageStore(
      transformedFieldResponse,
      stageStore,
      fieldResponse
   )

   customObjectModel.createOrUpdateFieldResponse(
      gofId,
      gofResponseId,
      transformedFieldResponse
   )
}

export const storeCustomObjectInCustomObjectStore = (
   props: StoreCustomObjectToCustomObjectModelProps
): CustomObjectModel => {
   const { customObjectId, customObjectStore, customObjectTemplateId } = props

   const customObject = {
      customObjectId,
      recordId: customObjectId,
      templateId: customObjectTemplateId,
      gofResponses: []
   }

   customObjectStore.createOrUpdateCustomObject(customObject)
   return customObjectStore.getCustomObject(customObjectId)
}

export const convertCustomObjectTemplateModelInputType = (
   customObjectTemplateResponse: Common_CustomObjectTemplate_FragmentFragment
): CustomObjectTemplateType => {
   const {
      pipelineItemTemplateId,
      gofs,
      assigneeFieldsMapping,
      customObjectTemplateProperties: properties
   } = customObjectTemplateResponse

   return {
      templateId: pipelineItemTemplateId,
      name: customObjectTemplateResponse.name,
      gofs: gofs.map(convertToGoFWithFieldsType),
      assigneeFieldsMapping: assigneeFieldsMapping,
      customObjectTemplateProperties: {
         pluralName: properties?.pluralName ?? '',
         primaryDisplayNameFieldId: properties?.primaryDisplayNameFieldId ?? '',
         singularName: properties?.singularName ?? ''
      },
      workflows: []
   }
}

export const storeCustomObjectTemplateModelInTemplateStore = (
   customObjectTemplate: Common_CustomObjectTemplate_FragmentFragment,
   customObjectTemplateStore: CustomObjectTemplateStore
) => {
   const templateId = customObjectTemplate.pipelineItemTemplateId
   const customObjectTemplateModelInput = convertCustomObjectTemplateModelInputType(
      customObjectTemplate
   )

   customObjectTemplateStore.createOrUpdateTemplate(
      customObjectTemplateModelInput
   )

   const customObjectTemplateModel = customObjectTemplateStore.getTemplate(
      templateId
   )
   customObjectTemplateModel.setTemplateDetailsStatus(
      TemplateDetailsStatusEnum.FetchedCompleteDetails
   )

   customObjectTemplateModel.setCanMergeRecords(
      customObjectTemplate.canMergeRecords
   )
}

export const storeAddAnotherGofResponsesInCustomObjectModel = (
   addAnotherGofResponses: AddAnotherGoFResult[],
   customObjectModel: CustomObjectModel,
   fieldsStore: FieldsStore
): void =>
   addAnotherGofResponses.forEach(gofResponseConfig => {
      gofResponseConfig.gofResponses.forEach(gofResponse => {
         const gofResponseData: GOFResponse = {
            gofId: gofResponse.gofId,
            gofResponseId: gofResponse.gofResponseId
         }

         customObjectModel.createOrUpdateGOFResponse(gofResponseData)

         gofResponse.fieldResponses.forEach(fieldResponse => {
            const fieldModel = fieldsStore.getField(fieldResponse.fieldId)

            const transformedFieldResponse = getTransformedFieldResponse(
               fieldModel,
               fieldResponse as FieldResponse
            )

            customObjectModel.createOrUpdateFieldResponse(
               gofResponse.gofId,
               gofResponse.gofResponseId,
               transformedFieldResponse
            )
         })
      })
   })

export const storeCustomObjectBaseDetailsInTemplateStore = (
   templateId: string,
   templateName: string,
   customObjectTemplateStore: CustomObjectTemplateStore,
   properties?: CustomObjectTemplateProperties
): void => {
   customObjectTemplateStore.createOrUpdateTemplate({
      name: templateName,
      templateId,
      customObjectTemplateProperties: {
         pluralName: properties?.pluralName ?? '',
         primaryDisplayNameFieldId: properties?.primaryDisplayNameFieldId ?? '',
         singularName: properties?.singularName ?? ''
      },
      assigneeFieldsMapping: [],
      gofs: [],
      workflows: []
   })
}
